import { useAuthStore } from '@/stores'
import methods from '@/utils/methods'
import { urqlMutation } from '@/utils/urqlMutation'

// Roles with an own-row permission on tokens (userID is a preset, so it is not sent)
const TOKEN_ROLES = ['user', 'staff', 'instanceAdmin']
const REGISTER_EVERY_MS = 7 * 24 * 60 * 60 * 1000
const CACHE_PREFIX = 'rs_push_token_'
const UNREGISTER_TIMEOUT_MS = 3000
const cacheKey = (type: string) => `${CACHE_PREFIX}${type}`

const readCache = (type: string) => {
  try {
    return JSON.parse(localStorage.getItem(cacheKey(type)) || 'null')
  } catch (error) {
    return null
  }
}

const writeCache = (type: string, value: Record<string, unknown>) => {
  try {
    localStorage.setItem(cacheKey(type), JSON.stringify(value))
  } catch (error) {
    // storage unavailable: register again next time
  }
}

const tokenRole = (authStore: ReturnType<typeof useAuthStore>) => {
  const defaultRole = authStore.account?.role
  return TOKEN_ROLES.includes(defaultRole) ? defaultRole : TOKEN_ROLES.find((r) => authStore.roles.includes(r))
}

const cachedTypes = () => {
  const types: string[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(CACHE_PREFIX)) types.push(key.slice(CACHE_PREFIX.length))
    }
  } catch (error) {
    // storage unavailable: nothing cached
  }
  return types
}

// Upserts the device's push/chat token for the signed-in user. Skips the write when this device
// already registered the same (token, userID) in the last 7 days.
export const registerToken = async (token: string, type: string) => {
  const authStore = useAuthStore()
  const userID = authStore.account?.id
  // App.vue registers again once the account is loaded
  if (!token || !userID) return
  const cached = readCache(type)
  if (cached?.token === token && cached?.userID === userID && Date.now() - cached.at < REGISTER_EVERY_MS) return
  const role = tokenRole(authStore)
  if (role) {
    await urqlMutation([{
      action: 'insert',
      table: 'tokens',
      data: { token, type },
    }], null, null, true, role)
  } else {
    // support / no-roles have no own-row token permission: still the backend route
    await methods.mutation([{
      action: 'insert',
      table: 'tokens',
      data: { userID, token, type },
    }], null, null, true, 'driverMutation')
  }
  writeCache(type, { token, userID, at: Date.now() })
}

// Forget every registration cached on this device; the next sign-in writes again
/** This device's push token (cached by registerToken), for endpoints that must reach this phone and not every phone of
 *  the account (a stream viewer: utils/streamSender). */
export const cachedPushToken = (type = 'fcm'): string | null => {
  const token = readCache(type)?.token
  return typeof token === 'string' && token ? token : null
}

export const clearTokenCache = () => {
  for (const type of cachedTypes()) {
    try {
      localStorage.removeItem(cacheKey(type))
    } catch (error) {
      // storage unavailable
    }
  }
}

const deleteToken = async (authStore: ReturnType<typeof useAuthStore>, token: string) => {
  try {
    const role = tokenRole(authStore)
    if (role) {
      await urqlMutation([{ action: 'delete', table: 'tokens', data: { token } }], null, null, true, role)
    } else {
      await methods.mutation([{ action: 'delete', table: 'tokens', data: { token } }], null, null, true, 'driverMutation')
    }
  } catch (error) {
    console.error('[pushToken] no pudimos borrar el token', error)
  }
}

// Logout: delete the token rows this device registered while the session can still authorize it
// (best effort, capped so a slow network never holds the sign-out), then clear the cache so the
// next account on this device registers again. Never throws.
export const unregisterTokensOnLogout = async (extraTokens: Array<string | null | undefined> = []) => {
  const tokens = new Set<string>()
  for (const type of cachedTypes()) {
    const cached = readCache(type)
    if (typeof cached?.token === 'string' && cached.token) tokens.add(cached.token)
  }
  for (const token of extraTokens) if (token) tokens.add(token)
  try {
    if (tokens.size) {
      const authStore = useAuthStore()
      await Promise.race([
        Promise.all([...tokens].map((token) => deleteToken(authStore, token))),
        new Promise((resolve) => setTimeout(resolve, UNREGISTER_TIMEOUT_MS)),
      ])
    }
  } catch (error) {
    console.error('[pushToken] no pudimos borrar los tokens', error)
  } finally {
    clearTokenCache()
  }
}
